import React, { useState, useEffect } from 'react';
import { Save, AlertTriangle, X, FileText } from 'lucide-react';
import { useToast } from '../../context/ToastContext';

const PromptEditorModal = ({ isOpen, onClose, roleKey, roleLabel, modelName }) => {
    const { toast } = useToast();
    const [promptText, setPromptText] = useState('');
    const [originalPrompt, setOriginalPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [variables, setVariables] = useState([]);

    // Determine task type from role key
    const taskType = roleKey === 'default_chat' ? 'chat' : roleKey;

    useEffect(() => {
        if (isOpen && roleKey) {
            fetchPrompt();
        }
    }, [isOpen, roleKey, modelName]);

    const fetchPrompt = async () => {
        setLoading(true);
        try {
            // Fetch all prompts and filter locally (not ideal but API is simple list for now)
            // Ideally we'd have /api/admin/ai/prompts?task_type=...
            const res = await fetch('/api/admin/ai/prompts');
            const prompts = await res.json();

            // Find active prompt for this task & model
            // 1. Try to find the exact model match first
            let active = prompts.find(p => p.task_type === taskType && p.model_name === modelName && p.is_active);
            
            // 2. If not found, fallback to default
            if (!active) {
                active = prompts.find(p => p.task_type === taskType && p.model_name === 'default' && p.is_active);
            }

            // Also check for task definition to get variables
            const tRes = await fetch('/api/admin/ai/tasks');
            const tasks = await tRes.json();
            const taskDef = tasks.find(t => t.task_type === taskType);

            setVariables(active?.variables || taskDef?.variables || []);
            setPromptText(active?.prompt_text || (active ? "" : taskDef?.default_prompt || ""));
            setOriginalPrompt(active?.prompt_text || "");

        } catch (err) {
            console.error("Failed to load prompt", err);
            toast.error("Could not load prompt configuration.");
        }
        setLoading(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                model_name: modelName || 'default',
                task_type: taskType,
                prompt_text: promptText,
                variables: variables,
                description: `Updated via Model Settings for ${roleLabel}`,
                is_active: true,
                locale: 'de'
            };

            const res = await fetch('/api/admin/ai/prompts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success("Prompt updated successfully.");
                onClose();
            } else {
                throw new Error("Failed to save");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to save prompt.");
        }
        setSaving(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-stone-900 border border-stone-700 w-full max-w-4xl rounded-xl shadow-2xl flex flex-col h-[80vh]">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-stone-800 bg-stone-900/50 rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-900/30 p-2 rounded text-blue-400">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-white">System Prompt: {roleLabel}</h3>
                            <p className="text-xs text-stone-500 font-mono">
                                Type: {taskType} | Model: {modelName || 'default'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone-500 hover:text-white p-1 rounded hover:bg-stone-800 transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col overflow-hidden">
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center text-stone-500">Loading prompt...</div>
                    ) : (
                        <>
                            <div className="flex-1 flex flex-col">
                                <label className="text-xs font-bold mb-2 text-stone-400 uppercase tracking-wide">System Prompt Instruction</label>
                                <textarea
                                    className="flex-1 w-full bg-black/30 border border-stone-700 rounded-lg p-4 text-stone-200 focus:border-blue-500 focus:outline-none font-mono text-sm leading-relaxed resize-none"
                                    value={promptText}
                                    onChange={(e) => setPromptText(e.target.value)}
                                    placeholder="Enter system instructions here..."
                                />
                            </div>

                            {/* Variables Helper */}
                            <div className="mt-4 bg-stone-950/50 border border-stone-800 p-3 rounded-lg">
                                <div className="text-xs font-bold text-stone-500 mb-2 uppercase">Available Variables (Injected by System)</div>
                                <div className="flex flex-wrap gap-2">
                                    {variables.map(v => (
                                        <span key={v} className="text-xs bg-stone-800 text-stone-300 px-2 py-1 rounded border border-stone-700 font-mono select-all cursor-help" title={`Use {{${v}}} in prompt`}>
                                            {`{{${v}}}`}
                                        </span>
                                    ))}
                                    {variables.length === 0 && <span className="text-xs text-stone-600 italic">No variables standard for this task.</span>}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-stone-800 bg-stone-900/50 rounded-b-xl flex justify-between items-center">
                    <div className="text-xs text-stone-500">
                        Changes normally take effect immediately for new requests.
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-stone-400 hover:text-white text-sm font-bold">
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading || saving}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-500 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
                            Save New Version
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromptEditorModal;
